export type ImageStylePair = {
  imageId: string
  imageStylePromptId: string
}

export type ImageStyleJob = ImageStylePair & {
  activityId: string
}

export type ApplyImageStyleParams = {
  imageIds: string[]
  imageStylePromptIds: string[]
  imageProvider?: string
  quality?: GenerationQuality
  numberOfImages?: number
}

export type ImageStyleBatchResult = {
  jobs: ImageStyleJob[]
  imageProvider?: string
  quality?: GenerationQuality
  numberOfImages?: number
}

export type ActiveImageStyleJob = {
  activityId: string
  shotId: string
  variantId: string
  sourceImageId: string
  imageStylePromptId: string
  status: ActivityStatus
}

export type ImageStyleBatchFailure = ImageStyleBatchResult & {
  failedPairs: ImageStylePair[]
  detail: string
  uncertainActivityId?: string | null
  imageProvider?: string
}

export function getImageStyleBatchFailure(error: unknown): ImageStyleBatchFailure | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) return
  const response = error.response
  if (typeof response !== 'object' || response === null || !('data' in response)) return
  const data: unknown = response.data
  if (
    typeof data !== 'object' ||
    data === null ||
    !('jobs' in data) ||
    !Array.isArray(data.jobs) ||
    !data.jobs.every(isImageStyleJob) ||
    !('failedPairs' in data) ||
    !Array.isArray(data.failedPairs) ||
    !data.failedPairs.every(isImageStylePair) ||
    !('detail' in data) ||
    typeof data.detail !== 'string' ||
    ('imageProvider' in data && typeof data.imageProvider !== 'string') ||
    ('quality' in data &&
      !Object.values(GenerationQuality).some((quality) => quality === data.quality)) ||
    ('numberOfImages' in data &&
      (typeof data.numberOfImages !== 'number' ||
        !Number.isInteger(data.numberOfImages) ||
        data.numberOfImages < 1 ||
        data.numberOfImages > 5)) ||
    ('uncertainActivityId' in data &&
      data.uncertainActivityId !== null &&
      typeof data.uncertainActivityId !== 'string')
  ) {
    return
  }
  return {
    jobs: data.jobs,
    failedPairs: data.failedPairs,
    detail: data.detail,
    ...('quality' in data && {
      quality: Object.values(GenerationQuality).find((quality) => quality === data.quality),
    }),
    ...('numberOfImages' in data &&
      typeof data.numberOfImages === 'number' && { numberOfImages: data.numberOfImages }),
    ...('imageProvider' in data &&
      typeof data.imageProvider === 'string' && { imageProvider: data.imageProvider }),
    ...('uncertainActivityId' in data && {
      uncertainActivityId:
        typeof data.uncertainActivityId === 'string' ? data.uncertainActivityId : null,
    }),
  }
}

function isImageStyleJob(value: unknown): value is ImageStyleJob {
  return isImageStylePair(value) && 'activityId' in value && typeof value.activityId === 'string'
}

function isImageStylePair(value: unknown): value is ImageStylePair {
  return (
    typeof value === 'object' &&
    value !== null &&
    'imageId' in value &&
    typeof value.imageId === 'string' &&
    'imageStylePromptId' in value &&
    typeof value.imageStylePromptId === 'string'
  )
}
import { GenerationQuality } from '../../data/types/GenerationParams'
import type { ActivityStatus } from '../enums/ActivityStatus'
